class RbDiamondFilterSlider extends HTMLElement {
    static get observedAttributes() {
        return ['min', 'max', 'step', 'labels', 'range-pivot', 'range-threshold', 'default-selected', 'caption-ranges'];
    }

    #boundHandlers = {};

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        this.isDragging = false;

        this.#boundHandlers = {
            updateRangeBar: this.updateRangeBar.bind(this),
            handleDragStart: this.handleDragStart.bind(this),
            handleDragEnd: this.handleDragEnd.bind(this),
            handleValueInput: this.handleValueInput.bind(this)
        };
    }

    connectedCallback() {
        if (!this._ready) {
            this.render();
            this.setupEventListeners();
            this._ready = true;
        }
        document.addEventListener('mouseup', this.#boundHandlers.handleDragEnd);
        document.addEventListener('touchend', this.#boundHandlers.handleDragEnd);
        this.updateRangeBar();
    }

    disconnectedCallback() {
        document.removeEventListener('mouseup', this.#boundHandlers.handleDragEnd);
        document.removeEventListener('touchend', this.#boundHandlers.handleDragEnd);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'default-selected' && this.startInput) {
            this.updateDefaultSelected(newValue);
            this.updateRangeBar();
        }
    }

    updateDefaultSelected(attributeValue) {
        if (!attributeValue) {
            return;
        }
        const defaultSelected = attributeValue.split(',');
        if (defaultSelected.length === 0) {
            return;
        }
        const labelValues = (this.getAttribute('label-values') || this.getAttribute('labels'))?.split(',');
        if (labelValues) {
            if (defaultSelected.length > 0) {
                this.startInput.value = labelValues.indexOf(defaultSelected[0]);;
                this.endInput.value = labelValues.indexOf(defaultSelected[defaultSelected.length - 1]) + 1;
            }
        } else if (!labelValues) {
            const startValue = parseFloat(defaultSelected[0]);
            const endValue = parseFloat(defaultSelected[defaultSelected.length - 1]);
            if (startValue) {
                this.startInput.value = this.getRawValueFromMapped(startValue);
                this.startValue.value = startValue;
            }
            if (endValue) {
                this.endInput.value = this.getRawValueFromMapped(endValue);
                this.endValue.value = endValue;
            }
        }
    }

    render() {
        const allLabels = this.getAttribute('labels')?.split(',');
        const min = parseFloat(this.getAttribute('min')) || 0;
        const max = parseFloat(this.getAttribute('max')) || 100;
        const step = parseFloat(this.getAttribute('step')) || 1;
        //range scale stuff
        this.rangePivot = this.clamp(
            parseFloat(this.getAttribute('range-pivot') || 0.5) * 100,
            1,
            99
        );

        this.rangeThreshold = this.clamp(
            parseFloat(this.getAttribute('range-threshold') || (min + ((max - min) / 2))),
            min + step,
            max - step
        );

        this.isBudget = this.getAttribute('is-budget') === 'true';

        // Filter labels to only include those within min and max range
        const labels = allLabels?.filter((_, index) => index >= min && index <= max);

        const tickCount = parseInt(this.getAttribute('tick-count'), 10);
        this.tickValues = [];
        if (!labels && tickCount > 1) {
            for (let i = 0; i < tickCount; i++) {
                this.tickValues.push(min + (max - min) * (i / (tickCount - 1)));
            }
        }

        const maxValue = labels ? labels.length : max;
        const sliderWidth = this.getAttribute('slider-width');

        const captionRanges = JSON.parse(this.getAttribute('caption-ranges') || '[]');
        const captionsHTML = captionRanges.map(range => {
            const width = ((range.to - range.from + 1) * (100 / maxValue));
            return (
                `<div class="caption-outline" style="width:${width}%">
                    <div></div>
                    <span>${range.caption}</span>
                </div>`
        )
        }).join('');

        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    margin-top:10px;
                    width: 100%;
                }

                .range-slider {
                    width: ${sliderWidth ? sliderWidth : "450px"};
                    position: relative;
                    margin: 0 auto;
                }

                .range-track {
                    height: 2px;
                    position: relative;
                    background: #e0e0e0;
                }

                .range-fill {
                    position: absolute;
                    height: 100%;
                    background-color: #333;
                    z-index: 1;
                }

                .step-markers {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                }

                .step-marker {
                    width: 2px;
                    height: 4px;
                    background: #fff;
                    transform: translateX(-50%);
                    position: absolute;
                    z-index: 2;
                    top: 0;
                }
                .step-marker:first-child {
                    width: 0px;
                }
                .step-marker:last-child {
                    width: 0px;
                }

                .range-input {
                    position: absolute;
                    width: calc(100% + 16px);
                    left: -8px;
                    border: none;
                    height: 2px;
                    background: none;
                    pointer-events: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    margin: 0;
                    top: 0;
                }

                .range-input::-webkit-slider-thumb {
                    height: 20px;
                    width: 20px;
                    border-radius: 50%;
                    background: #fff;
                    border: 1px solid #222;
                    pointer-events: auto;
                    -webkit-appearance: none;
                    cursor: pointer;
                    z-index: 2;
                    position: relative;
                }

                .range-input::-webkit-slider-thumb:active {
                    z-index: 1;
                }

                .range-input::-moz-range-thumb { 
                    height: 20px;
                    width: 20px;
                    border-radius: 50%;
                    background: #fff;
                    border: 1px solid #222;
                    pointer-events: auto;
                    -moz-appearance: none;
                    cursor: pointer;
                    z-index: 2;
                    position: relative;
                }

                .range-input::-moz-range-thumb:active {
                    z-index: 1;
                }

                .range-labels {
                    position: relative;
                    width: 100%;
                    margin-bottom: 25px;
                }

                .range-labels span {
                    position: absolute;
                    transform: translateX(-50%);
                    font-size: 12px;
                    top: 12px;
                    font-weight: bold;
                    white-space: nowrap;
                    color: grey;
                    transition: color 0.2s ease;
                }

                .range-labels span.selected {
                    color: #1b2230;
                }

                .range-labels--ticks {
                    height: 28px;
                    margin-bottom: 4px;
                }

                .range-labels--ticks span {
                    font-size: 10px;
                    font-weight: 500;
                    top: 10px;
                }

                .range-labels--ticks span:first-child {
                    transform: none;
                }

                .range-labels--ticks span:last-child {
                    transform: translateX(-100%);
                }

                .value-inputs {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 20px;
                    width: 100%;
                }

                .value-input {
                    padding: 5px;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    min-width: 75px;
                    font-size: 12px;
                }

                .value-input::-webkit-inner-spin-button,
                .value-input::-webkit-outer-spin-button {
                    opacity: 1;
                    -webkit-appearance: inner-spin-button;
                    appearance: inner-spin-button;
                }

                .caption-outlines {
                    display: flex;
                    width: 100%;
                    font-weight: 500;
                    font-size: 9px;
                    gap: 5px;
                    padding-top: 5px;
                    color:#9E9E9E;
                }
                .caption-outline {
                    position: relative;
                }
                .caption-outline div {
                    border: 1px solid #9E9E9E;
                    margin-bottom:7px;
                    border-top:none;
                    height: 10px;
                    width: 100%;
                }
                .caption-outline span {
                   display:block;
                   text-align:center;

                }

                @media (max-width: 1230px) {
                    .range-slider {
                        width: ${sliderWidth ? sliderWidth : "375px"};
                    }
                }
            </style>

            <div class="range-slider">
                <div class="range-track">
                    <div class="range-fill"></div>
                    ${labels ? `
                        <div class="step-markers">
                            ${Array(labels.length + 1).fill().map((_, i) => `
                                <div class="step-marker" style="left: ${i * (100 / labels.length)}%"></div>
                            `).join('')}
                        </div>
                    ` : this.tickValues.length ? `
                        <div class="step-markers">
                            ${this.tickValues.map((_, i) => `
                                <div class="step-marker" style="left: ${(i / (this.tickValues.length - 1)) * 100}%"></div>
                            `).join('')}
                        </div>
                    ` : ''}
                    <input type="range" class="range-input start" min="${min}" max="${maxValue}" value="${min}" step="${step}">
                    <input type="range" class="range-input end" min="${0}" max="${maxValue}" value="${maxValue}" step="${step}">
                </div>
                ${labels ? `
                    <div class="range-labels">
                        ${labels.map((label, i) => `
                            <span style="left: ${(i * (100 / labels.length)) + (50 / labels.length)}%">${label}</span>
                        `).join('')}
                    </div>
                    <div class="caption-outlines">${captionsHTML}</div> 
                ` : `
                    ${this.tickValues.length ? `
                        <div class="range-labels range-labels--ticks">
                            ${this.tickValues.map((value, i) => `
                                <span style="left: ${(i / (this.tickValues.length - 1)) * 100}%">${this.formatTickLabel(value, max)}</span>
                            `).join('')}
                        </div>
                    ` : ''}
                    <div class="value-inputs">
                        <input type="number" class="value-input start-value" value="${min}" min="${min}" max="${max}" step="${step}">
                        <input type="number" class="value-input end-value" value="${max}" min="${min}" max="${max}" step="${step}">
                    </div>
                `}
            </div>
        `;


        this.cacheElements();
    }

    cacheElements() {
        this.startInput = this.shadowRoot.querySelector('.range-input.start');
        this.endInput = this.shadowRoot.querySelector('.range-input.end');
        this.rangeFill = this.shadowRoot.querySelector('.range-fill');

        if (!this.getAttribute('labels')) {
            this.startValue = this.shadowRoot.querySelector('.start-value');
            this.endValue = this.shadowRoot.querySelector('.end-value');
        }

        this.rangeLabels = this.shadowRoot.querySelectorAll('.range-labels span');

        this.updateDefaultSelected(this.getAttribute('default-selected'))
    }

    setupEventListeners() {
        this.startInput.addEventListener('input', this.#boundHandlers.updateRangeBar);
        this.endInput.addEventListener('input', this.#boundHandlers.updateRangeBar);

        this.startInput.addEventListener('mousedown', this.#boundHandlers.handleDragStart);
        this.endInput.addEventListener('mousedown', this.#boundHandlers.handleDragStart);
        this.startInput.addEventListener('touchstart', this.#boundHandlers.handleDragStart);
        this.endInput.addEventListener('touchstart', this.#boundHandlers.handleDragStart);

        if (!this.getAttribute('labels')) {
            this.startValue.addEventListener('change', this.#boundHandlers.handleValueInput);
            this.endValue.addEventListener('change', this.#boundHandlers.handleValueInput);
        }
    }

    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max)
    }

    formatTickLabel(value, max) {
        if (max >= 1000) {
            if (value >= 1000000) {
                const m = value / 1000000;
                return (Number.isInteger(m) ? m : m.toFixed(1)) + 'm';
            }
            const k = value / 1000;
            return (k >= 10 || Number.isInteger(k) ? Math.round(k) : k.toFixed(1)) + 'k';
        }
        return String(Math.round(value * 10) / 10);
    }

    getMappedValue(value) {
        // Get the slider's native min and max values.
        const minVal = parseFloat(this.startInput.min);
        const maxVal = parseFloat(this.startInput.max);
        let stepVal = parseFloat(this.startValue.step);
        // Define the custom mapping.
        // Here, 0% corresponds to minVal, 75% to tenthMax, and 100% to maxVal.
        let rangeMapping = [];
        if (this.isBudget) {
            rangeMapping = [
                { pos: 0, value: minVal, step: 500 },
                { pos: 40, value: 30000, step: 1000 },
                { pos: 70, value: 100000, step: 50000 },
                { pos: 90, value: 300000, step: 100_000 },
                { pos: 100, value: maxVal }
            ];
        } else {
            rangeMapping = [
                { pos: 0, value: minVal },
                { pos: this.rangePivot, value: this.rangeThreshold },
                { pos: 100, value: maxVal }
            ];
        }

        // Normalize the input value to a percentage (0–100) based on the native range.
        const normalizedPct = ((value - minVal) / (maxVal - minVal)) * 100;

        // Find the segment in the mapping where the normalized percentage lies,
        // then interpolate to compute the mapped value.
        for (let i = 0; i < rangeMapping.length - 1; i++) {
            const startMapping = rangeMapping[i];
            const endMapping = rangeMapping[i + 1];
            if (normalizedPct >= startMapping.pos && normalizedPct <= endMapping.pos) {
                const factor = (normalizedPct - startMapping.pos) / (endMapping.pos - startMapping.pos);
                const finalValue = startMapping.value + factor * (endMapping.value - startMapping.value);

                if (startMapping.step) {
                    stepVal = startMapping.step;
                }

                const roundedValue = Math.round(finalValue / stepVal) * stepVal;
                return roundedValue < minVal ? minVal : roundedValue;
            }
        }
        return value; // Fallback in case the value is out of bounds.
    }



    // Inverse mapping: given a mapped (real) value, return the corresponding raw slider value.
    getRawValueFromMapped(mappedValue) {
        // Retrieve native slider min and max.
        const minVal = parseFloat(this.startInput.min);
        const maxVal = parseFloat(this.startInput.max);

        let rangeMapping = [];
        if (this.isBudget) {
            rangeMapping = [
                { pos: 0, value: minVal },
                { pos: 40, value: 30000 },
                { pos: 70, value: 100000 },
                { pos: 90, value: 300000 },
                { pos: 100, value: maxVal }
            ];
        } else {
            rangeMapping = [
                { pos: 0, value: minVal },
                { pos: this.rangePivot, value: this.rangeThreshold },
                { pos: 100, value: maxVal }
            ];
        }

        // Clamp the mapped value within valid boundaries.
        mappedValue = this.clamp(mappedValue, minVal, maxVal);

        // Find the segment in which the mapped value falls.
        for (let i = 0; i < rangeMapping.length - 1; i++) {
            const startMapping = rangeMapping[i];
            const endMapping = rangeMapping[i + 1];
            if (mappedValue >= startMapping.value && mappedValue <= endMapping.value) {
                // Compute the interpolation factor within this segment.
                const factor = (mappedValue - startMapping.value) / (endMapping.value - startMapping.value);
                // Calculate the raw percentage that would produce this mapped value.
                const rawPct = startMapping.pos + factor * (endMapping.pos - startMapping.pos);
                // Convert the raw percentage back into a raw slider value.
                return minVal + (rawPct / 100) * (maxVal - minVal);
            }
        }
        // Fallback if no segment matches.
        return mappedValue;
    }


    handleDragStart(e) {
        this.isDragging = true;
        const input = e.target;
        input.dataset.originalStep = input.step;
        input.step = 'any';
    }

    handleDragEnd(e) {
        if (!this.isDragging) return;

        this.isDragging = false;

        [this.startInput, this.endInput].forEach(input => {
            if (input.dataset.originalStep) {
                const step = parseFloat(input.dataset.originalStep);
                const value = parseFloat(input.value);
                const snappedValue = Math.round(value / step) * step;
                input.step = input.dataset.originalStep;
                input.value = snappedValue;
                delete input.dataset.originalStep;
            }
        });
        this.updateRangeBar();
        this.emitChangeEvent();
    }

    handleValueInput(e) {
        const isStart = e.target.classList.contains('start-value');
        const value = parseFloat(e.target.value);
        const min = parseFloat(e.target.min);
        const max = parseFloat(e.target.max);

        const startVal = parseFloat(this.startValue.value);
        const endVal = parseFloat(this.endValue.value);

        const clampedMappedValue = this.clamp(value, min, max);
        e.target.value = clampedMappedValue;
        const rawValue = this.getRawValueFromMapped(clampedMappedValue);

        if (isStart) {
            if (value > endVal) {
                this.endInput.value = rawValue;
            }

            this.startInput.value = rawValue;
        } else {
            if (value < startVal) {
                this.startInput.value = rawValue;
            }
            this.endInput.value = rawValue;
        }

        this.updateRangeBar(true);
        this.emitChangeEvent();
    }


    updateRangeBar(isValueInput = false) {
        const start = parseFloat(this.startInput.value);
        const end = parseFloat(this.endInput.value);

        if (start > end) {
            [this.startInput.value, this.endInput.value] = [end, start];
        }


        let mappedStart = start
        let mappedEnd = end


        if (this.startValue) {
            mappedStart = this.getMappedValue(start);
            mappedEnd = this.getMappedValue(end);
        }

        const sliderMin = parseFloat(this.startInput.min)
        const sliderMax = parseFloat(this.startInput.max)

        const sliderRangeDiff = sliderMax - sliderMin

        const percentageStart = ((start - sliderMin) / (sliderRangeDiff)) * 100;
        const percentageEnd = ((end - sliderMin) / (sliderRangeDiff)) * 100;

        this.rangeFill.style.left = percentageStart + '%';
        this.rangeFill.style.width = (percentageEnd - percentageStart) + '%';


        if (this.rangeLabels.length) {
            this.rangeLabels.forEach((label, index) => {
                const inRange = this.tickValues.length
                    ? this.tickValues[index] >= mappedStart && this.tickValues[index] <= mappedEnd
                    : index >= start && index < end;
                label.classList.toggle('selected', inRange);
            });
        }

        // Update number inputs if they exist
        if (isValueInput !== true && this.startValue && this.endValue) {
            const valueStep = parseFloat(this.startValue.step);
            const decimalPlaces = valueStep.toString().includes('.') ?
                valueStep.toString().split('.')[1].length : 0;
            this.startValue.value = mappedStart.toFixed(decimalPlaces);
            this.endValue.value = mappedEnd.toFixed(decimalPlaces);
        }
    }

    emitChangeEvent() {
        let minSelected = parseFloat(this.startInput.value);
        let maxSelected = parseFloat(this.endInput.value);

        if (this.startValue && this.endValue) {
            minSelected = parseFloat(this.startValue.value);
            maxSelected = parseFloat(this.endValue.value)
        }

        const labelValues = (this.getAttribute('label-values') || this.getAttribute('labels'))?.split(',');
        const labelSelected = labelValues?.filter((_, index) => index >= minSelected && index < maxSelected);

        console.log(this.getAttribute('labels'));
        console.log(labelSelected, minSelected, maxSelected);

        const min = parseFloat(this.startInput.min);
        const max = parseFloat(this.endInput.max);

        console.log(min, max);

        this.dispatchEvent(new CustomEvent('valueChange', {
            detail: {
                minSelected,
                maxSelected,
                labelSelected,
                min,
                max
            },
            bubbles: true,
            composed: true
        }));
    }
}

customElements.define('rb-diamond-filter-slider', RbDiamondFilterSlider);